import React from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Button,
    FormControlLabel,
    Checkbox,
    useTheme,
    Paper
} from '@mui/material';

import { FilterState } from './ModelDataSampleListTypes';

interface ModelDataSampleListFilterProps {
    filterState: FilterState;
    onFilterChange: (field: keyof FilterState, value: string | boolean) => void;
    onApplyFilter: () => void;
    onResetFilter: () => void;
    unitConfig: any;
    preferredUnits: {
        temperature: string;
        flowrate: string;
        pressure: string;
    };
}

/**
 * Filter component for ModelDataSampleList that aligns with backend filtering capabilities
 */
export const ModelDataSampleListFilter: React.FC<ModelDataSampleListFilterProps> = ({
    filterState,
    onFilterChange,
    onApplyFilter,
    onResetFilter,
    unitConfig,
    preferredUnits
}) => {
    const theme = useTheme();

    return (
        <Paper 
            elevation={2}
            sx={{
                padding: 3,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                marginBottom: 3
            }}
        >
            <Grid container spacing={4}>
                {/* Hot Media Section */}
                <Grid item xs={12} md={6} lg={5}>
                    <Box sx={{ 
                        backgroundColor: 'rgba(255, 100, 100, 0.05)', 
                        padding: 2, 
                        borderRadius: 1,
                        border: '1px solid rgba(255, 100, 100, 0.2)'
                    }}>
                        <Typography 
                            variant="subtitle1" 
                            color="error"
                            gutterBottom
                            fontWeight="medium"
                            sx={{ borderBottom: '1px solid rgba(255, 100, 100, 0.2)', pb: 1, mb: 2 }}
                        >
                            {unitConfig?.hotMediaType || 'Hot Media'} Parameters
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Hot Media Flowrate */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Flow Rate ({preferredUnits.flowrate})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromHotMediaFlowRate}
                                            onChange={(e) => onFilterChange('fromHotMediaFlowRate', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.flowrate}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toHotMediaFlowRate}
                                            onChange={(e) => onFilterChange('toHotMediaFlowRate', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.flowrate}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Hot Media Temperature In */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Inlet Temperature ({preferredUnits.temperature})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromHotMediaTempIn}
                                            onChange={(e) => onFilterChange('fromHotMediaTempIn', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toHotMediaTempIn}
                                            onChange={(e) => onFilterChange('toHotMediaTempIn', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Hot Media Temperature Out */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Outlet Temperature ({preferredUnits.temperature})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromHotMediaTempOut}
                                            onChange={(e) => onFilterChange('fromHotMediaTempOut', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toHotMediaTempOut}
                                            onChange={(e) => onFilterChange('toHotMediaTempOut', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Hot Media Pressure Drop */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Pressure Drop ({preferredUnits.pressure})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromHotMediaPressureDrop}
                                            onChange={(e) => onFilterChange('fromHotMediaPressureDrop', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.pressure}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toHotMediaPressureDrop}
                                            onChange={(e) => onFilterChange('toHotMediaPressureDrop', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.pressure}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>

                {/* Cold Media Section */}
                <Grid item xs={12} md={6} lg={5}>
                    <Box sx={{ 
                        backgroundColor: 'rgba(100, 100, 255, 0.05)', 
                        padding: 2, 
                        borderRadius: 1,
                        border: '1px solid rgba(100, 100, 255, 0.2)'
                    }}>
                        <Typography 
                            variant="subtitle1" 
                            color="primary"
                            gutterBottom
                            fontWeight="medium"
                            sx={{ borderBottom: '1px solid rgba(100, 100, 255, 0.2)', pb: 1, mb: 2 }}
                        >
                            {unitConfig?.coldMediaType || 'Cold Media'} Parameters
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Cold Media Flowrate */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Flow Rate ({preferredUnits.flowrate})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromColdMediaFlowRate}
                                            onChange={(e) => onFilterChange('fromColdMediaFlowRate', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.flowrate}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toColdMediaFlowRate}
                                            onChange={(e) => onFilterChange('toColdMediaFlowRate', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.flowrate}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Cold Media Temperature In */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Inlet Temperature ({preferredUnits.temperature})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromColdMediaTempIn}
                                            onChange={(e) => onFilterChange('fromColdMediaTempIn', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toColdMediaTempIn}
                                            onChange={(e) => onFilterChange('toColdMediaTempIn', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Cold Media Temperature Out */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Outlet Temperature ({preferredUnits.temperature})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromColdMediaTempOut}
                                            onChange={(e) => onFilterChange('fromColdMediaTempOut', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toColdMediaTempOut}
                                            onChange={(e) => onFilterChange('toColdMediaTempOut', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.temperature}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Cold Media Pressure Drop */}
                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    Pressure Drop ({preferredUnits.pressure})
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="From"
                                            type="number"
                                            value={filterState.fromColdMediaPressureDrop}
                                            onChange={(e) => onFilterChange('fromColdMediaPressureDrop', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.pressure}</Typography>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="To"
                                            type="number"
                                            value={filterState.toColdMediaPressureDrop}
                                            onChange={(e) => onFilterChange('toColdMediaPressureDrop', e.target.value)}
                                            InputProps={{
                                                endAdornment: <Typography variant="caption" color="text.secondary">{preferredUnits.pressure}</Typography>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>

                {/* Actions Section */}
                <Grid item xs={12} md={12} lg={2}>
                    <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'space-between',
                        padding: 2
                    }}>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterState.onlyTodo}
                                        onChange={(e) => {
                                            console.log('Todo checkbox clicked, new value:', e.target.checked);
                                            onFilterChange('onlyTodo', e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                }
                                label="Show only To-do items"
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={onApplyFilter}
                                fullWidth
                            >
                                Apply Filter
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={onResetFilter}
                                fullWidth
                            >
                                Reset Filters
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
}; 